import { SearchParams } from "@/shared/search/search.types";
import { useLazyQuery } from "@vue/apollo-composable";
import { computed, Ref, watchEffect } from "vue";
import { generateSearchDocumentsQuery } from "../../graphql/documentation-search.query";
import { DataSearchConfigId, useSearchConfig } from "@/shared/search/search-config";
import { generateQueryFilterForSearch } from "@/shared/facets/facets-filter-generator";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import { Documentation } from "@/shared/documentation/documentation.types.ts";
import { mapContentEdgesToDocumentModel } from "@/shared/documentation/mapper/document.mapper.ts";
import { INIT_LOAD_AMOUNT } from "@/shared/environment/consts.ts";
import { useLanguageStore } from "@/shared/store/language-store";
import { useDocumentationDisplayConfig } from "./documentation-display-config.ts";
import { isSortResolved } from "@/shared/components/sort-dropdown/sort-dropdown.model.ts";
import { toContentSort } from "./content-sort.ts";

export interface DocumentSearchParams extends SearchParams {
    /** Gates the search query itself, so callers can avoid firing it when its result is not used. Default is true. */
    enabled?: Ref<boolean>;
}

export const useDocumentSearch = ({
    selected,
    searchPhrase,
    sort,
    searchContext,
    count,
    showFirstRelevantPropertyItemOnly,
    enabled,
}: DocumentSearchParams) => {
    const languageStore = useLanguageStore();

    const isEnabled = computed(() => enabled?.value ?? true);

    //first fetch search configuration for document search
    const { result: documentSearchConfigResult, loading: documentSearchConfigLoading } = useSearchConfig(
        DataSearchConfigId.documentSelection
    );
    const { hasAccess: fuzzy } = useHasAccess({ featureID: AccessFeature.fuzzy_search });

    //first fetch display configuration for document search
    const {
        result: dataDisplayResult,
        loading: dataConfigLoading,
        getDataDisplayPropertiesByConfig,
    } = useDocumentationDisplayConfig();

    const searchDocumentsQuery = computed(() => {
        if (!dataDisplayResult.value) return generateSearchDocumentsQuery({}, {}, {}, searchContext?.value);

        return generateSearchDocumentsQuery(
            dataDisplayResult.value.datafieldsFragment.datafields,
            dataDisplayResult.value.datafieldsFragment.localizationDatafields,
            dataDisplayResult.value.nodeFieldsDataFragment,
            searchContext?.value
        );
    });

    const searchDocumentsFilter = computed(() => {
        const { assetFilter, contentFilter, productId, mechanicId, articleId } = generateQueryFilterForSearch(
            selected?.value ?? [],
            fuzzy.value,
            searchContext?.value,
            documentSearchConfigResult.value?.searchEntries ?? [],
            searchPhrase?.value
        );

        return {
            assetFilter,
            productId,
            mechanicId,
            contentFilter,
            articleId,
            acceptedLanguages: languageStore.dataQueryLanguages,
            sort: toContentSort(sort?.value),
            after: "",
            first: count ?? INIT_LOAD_AMOUNT,
        };
    });

    const {
        loading: searchLoading,
        error: searchError,
        result: searchResult,
        fetchMore,
        load,
    } = useLazyQuery(
        searchDocumentsQuery,
        searchDocumentsFilter,
        computed(() => ({ enabled: isEnabled.value }))
    );

    const stop = watchEffect(
        () => {
            if (!isEnabled.value || !documentSearchConfigResult.value || !isSortResolved(sort)) return;

            load();
            stop();
        },
        { flush: "post" }
    );

    const loading = computed(
        () => (isEnabled.value && searchLoading.value) || documentSearchConfigLoading.value || dataConfigLoading.value
    );

    const error = computed(() => {
        return searchError.value;
    });

    /**
     * The searched contents are reachable under a different path for every search context, because the
     * query is built against the matching root field (`mechanic`, `assets`, `product`, `article` or the
     * plain `contents`). This resolves the context specific path once, so that result, total and
     * pagination all read the same node.
     *
     * @param data - A raw result of the generated search documents query.
     * @returns The related contents of the current search context.
     */
    const selectRelatedContents = (data: typeof searchResult.value) => {
        if (searchContext?.value.mechanic) return data?.mechanic?.relatedContents;
        if (searchContext?.value.asset && searchContext?.value.product)
            return data?.assets?.assets[0]?.node.relatedContents;
        if (searchContext?.value.product) return data?.product?.relatedContents;
        if (searchContext?.value.article) return data?.article?.relatedContents;

        return data?.contents;
    };

    const result = computed(() => ({ contents: selectRelatedContents(searchResult.value) }));

    const contents = computed<Documentation[] | undefined>(() => {
        if (result.value?.contents?.contents) {
            const documentModels = mapContentEdgesToDocumentModel(
                result.value?.contents.contents,
                getDataDisplayPropertiesByConfig
            );

            if (showFirstRelevantPropertyItemOnly && documentModels.length > 0) {
                //Only one metadata item should be displayed:
                //the first priority property item, otherwise the first property item
                documentModels.forEach((x) => {
                    const firstPriorityElement = x.propertyItems.find((item) => item.isPriority);
                    if (firstPriorityElement) x.propertyItems = [firstPriorityElement];
                    else x.propertyItems = [x.propertyItems[0]];
                });
            }

            return documentModels;
        }

        return undefined;
    });

    const total = computed(() => selectRelatedContents(searchResult.value)?.total);

    const searchFields = computed(() => {
        return documentSearchConfigResult.value?.searchEntries ?? [];
    });

    /**
     * Loads the next page of contents and appends it to the current result.
     *
     * @param after - Cursor of the last already loaded content.
     * @param first - Number of contents to load.
     * @returns The cursor of the last loaded content, or `undefined` if no further content was returned.
     */
    const loadMoreContents = async (after: string, first: number): Promise<string | undefined> => {
        const fetchResult = await fetchMore({ variables: { after, first } });

        const contentEdges = selectRelatedContents(fetchResult?.data)?.contents;
        if (!contentEdges || contentEdges.length === 0) return undefined;

        return contentEdges[contentEdges.length - 1].cursor;
    };

    return {
        contents,
        result,
        total,
        searchFields,
        loading,
        error,
        loadMoreContents,
    };
};
